import "./global-shim";
import React from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import $ from "jquery";
import "./assets/scss/tailwind.scss";
import "./assets/scss/style.scss";
import "react-datepicker/dist/react-datepicker.css";
import "reactjs-popup/dist/index.css";
import "clevai-ui/dist/index.css";
import { APP_NAME } from "./envVariable";

// Summernote math plugin checks `window.jQuery` at evaluation and bails
// if missing, which breaks the question editor toolbar. Assign here in
// the entry body — App and custom-summernote are imported dynamically
// below, so their chunks evaluate after this runs regardless of how
// Rollup orders side-effect imports in the production bundle.
(window as any).$ = $;
(window as any).jQuery = $;

const appName = APP_NAME || "admin";
console.log("[admin MFE] module load; APP_NAME =", APP_NAME, "→ using:", appName);

// Standalone admin/index.html loads KaTeX + render.js defining window.renderTeX.
// When portal embeds admin, portal's index.html is used instead, so renderTeX
// is undefined and TeXPreview crashes. Inject the assets at runtime from admin's
// own origin (resolved via import.meta.url because portal is cross-origin).
const ensureTeX = (): Promise<void> => {
  if (typeof (window as any).renderTeX === "function") return Promise.resolve();
  // Resolve via origin only — Vite's asset-import-meta-url plugin tries to
  // statically resolve `new URL('<literal>', import.meta.url)` as a build
  // asset, which fails for paths that escape src/. Per ARCHITECTURE.md §3,
  // each MFE is served at its own subdomain root so origin + /TeX/ is correct.
  const base = new URL(import.meta.url).origin + "/";
  const addLink = (href: string) => {
    if (document.querySelector(`link[data-admin-tex="${href}"]`)) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = href;
    l.setAttribute("data-admin-tex", href);
    document.head.appendChild(l);
  };
  const addScript = (src: string) =>
    new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(
        `script[data-admin-tex="${src}"]`
      ) as HTMLScriptElement | null;
      if (existing) {
        if ((existing as any)._loaded) return resolve();
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () =>
          reject(new Error(`Failed to load ${src}`))
        );
        return;
      }
      const s = document.createElement("script");
      s.src = src;
      s.async = false;
      s.setAttribute("data-admin-tex", src);
      s.onload = () => {
        (s as any)._loaded = true;
        resolve();
      };
      s.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(s);
    });
  addLink(`${base}TeX/katex.min.css`);
  addLink(`${base}TeX/custom.css`);
  return addScript(`${base}TeX/katex.min.js`)
    .then(() => addScript(`${base}TeX/contrib/mhchem.min.js`))
    .then(() => addScript(`${base}TeX/contrib/auto-render.min.js`))
    .then(() => addScript(`${base}TeX/render.js`));
};

/* React 18 root tracking — keep reference per mount so unmount()
   tears down the concurrent tree cleanly instead of leaking a root. */
let mfeRoot: Root | null = null;
let standaloneRoot: Root | null = null;

// Expose MFE bridge SYNCHRONOUSLY so portal sees window.render${appName}
// as soon as this script module finishes evaluating. App is imported
// dynamically inside the handler: if App throws we surface a clear error
// rather than silently failing module evaluation (which would leave the
// bridge unassigned and produce portal's generic "not a function" error).
window[`render${appName}`] = async (containerId, history, authStore) => {
  try {
    await ensureTeX();
    const { default: App } = await import("./App");
    const el = document.getElementById(containerId);
    if (!el) {
      console.warn(`[${appName} MFE] container #${containerId} not found`);
      return;
    }
    mfeRoot = createRoot(el);
    mfeRoot.render(<App history={history} authStore={authStore} />);
  } catch (err) {
    console.error("[admin MFE] render failed:", err);
    throw err;
  }
};

window[`unmount${appName}`] = (_containerId) => {
  mfeRoot?.unmount();
  mfeRoot = null;
};

console.log("[admin MFE] bridge set:", `render${appName}`);

// Standalone fallback — mount into #root ONLY when not embedded in portal.
// Guards prevent nuking portal's React tree if appName is misconfigured.
const rootEl = document.getElementById("root");
const isStandalone =
  !document.getElementById(`${appName}-container`) &&
  rootEl &&
  !rootEl.hasChildNodes();

if (isStandalone) {
  import("./App").then(({ default: App }) => {
    standaloneRoot = createRoot(rootEl!);
    standaloneRoot.render(<App />);
  });
}
